/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/coldefs/metadata/molecule/safety
 * @desc Column Definition for metadata molecule safety
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import MoleculeSafety from 'labstep-web/components/Molecule/Safety';
import { MoleculeSafetyPictograms as Pictograms } from 'labstep-web/components/Molecule/Safety/Pictograms';
import { fieldName } from 'labstep-web/components/PubChemMetadata/Form/fields';
import { ICONS } from 'labstep-web/constants/icons';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellEditorParams,
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import { stopEditingAndFocus } from 'labstep-web/core/DataGrid/utils';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolValue/components/ColumnHeader';
import { GridSmartTableProtocolValueService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value.service';
import type { Entity } from 'labstep-web/models/entity.model';
import type { Experiment } from 'labstep-web/models/experiment.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { Resource } from 'labstep-web/models/resource.model';
import { PubchemService } from 'labstep-web/services/pubchem.service';

export const getMetadataMoleculeSafetyColDef = (
  experimentOrProtocol: Experiment | Protocol,
  parentEntityClass: string,
  metadataType: MetadataType,
  metadataLabel: string,
  protocolValueName?: string,
): SmartTableColDef => {
  return {
    colId: protocolValueName
      ? `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}:safety:${protocolValueName}`
      : `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}:safety`,
    sortable: true,
    headerName: 'Safety',
    headerComponent: GridSmartTableColumnHeader,
    headerComponentParams: (params: HeaderComponentParamsProps) => {
      return {
        icon: protocolValueName
          ? ICONS.metadata.secondary
          : ICONS.metadata.primary,
        cornerIcon:
          protocolValueName &&
          (parentEntityClass === Resource.entityName
            ? ICONS.resource.primary
            : ICONS.resource_item.primary),
        secondaryHeader:
          protocolValueName &&
          `${protocolValueName}${GridSmartTableProtocolValueService.getMetadataParentPrefix(parentEntityClass)}`,
        toolPanelColumnName: `${GridSmartTableProtocolValueService.getMetadataParentPrefix(parentEntityClass)}${metadataLabel} - Safety`,
        experimentOrProtocol,
      };
    },
    editable: (params: EditableCallbackParams) =>
      GridSmartTableProtocolValueService.getMetadataEditable(
        params,
        experimentOrProtocol,
        metadataType,
        metadataLabel,
        parentEntityClass,
        protocolValueName,
      ),
    valueGetter: (params: ValueGetterParams): string | null => {
      return null;
    },
    valueSetter: (params: ValueSetterParams): boolean => {
      return false;
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      if (!GridSmartTableProtocolValueService.isEditable(params)) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (!metadata) {
        return (
          <TextPlaceholder
            onClick={() => {
              GridSmartTableProtocolValueService.createMetadata(
                params,
                experimentOrProtocol,
                parentEntityClass,
                metadataType,
                metadataLabel,
                protocolValueName,
                { molecule: { name: 'Untitled structure' } },
              );
            }}
          >
            Create structure
          </TextPlaceholder>
        );
      }

      if (!metadata.molecule?.properties) {
        return (
          <DataGridPlaceholder
            params={params}
            editable={GridSmartTableProtocolValueService.getMetadataEditable(
              params,
              experimentOrProtocol,
              metadataType,
              metadataLabel,
              parentEntityClass,
              protocolValueName,
            )}
            placeholder={
              GridSmartTableProtocolValueService.getMetadataEditable(
                params,
                experimentOrProtocol,
                metadataType,
                metadataLabel,
                parentEntityClass,
                protocolValueName,
              )
                ? 'Enter'
                : ' '
            }
          />
        );
      }

      if (
        metadata.molecule?.properties.Safety.pictograms?.length === 0
      ) {
        return (
          <DataGridPlaceholder
            params={params}
            editable={GridSmartTableProtocolValueService.getMetadataEditable(
              params,
              experimentOrProtocol,
              metadataType,
              metadataLabel,
              parentEntityClass,
              protocolValueName,
            )}
            placeholder={
              GridSmartTableProtocolValueService.getMetadataEditable(
                params,
                experimentOrProtocol,
                metadataType,
                metadataLabel,
                parentEntityClass,
                protocolValueName,
              )
                ? 'Enter'
                : ' '
            }
          />
        );
      }

      return metadata.molecule ? (
        <Pictograms
          pubChemMetadata={metadata.molecule.properties}
          isInline
          size="mini"
        />
      ) : null;
    },
    cellEditorPopup: true,
    cellEditor: (params: CellEditorParams<Entity>) => {
      if (!params.node) {
        return '';
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (!metadata) {
        return null;
      }

      if (metadata.molecule && !metadata.molecule.properties) {
        return GridSmartTableProtocolValueService.updateEntity(
          params,
          metadata.molecule.entityName,
          metadata.molecule.guid,
          {
            pubchem: PubchemService.getPubchemBody(
              metadata.molecule,
              fieldName,
              '',
            ),
          },
        );
      }

      return metadata.molecule ? (
        <MoleculeSafety
          parent={metadata.molecule}
          pubChemMetadata={metadata.molecule.properties!}
          modalProps={{
            open: true,
            onClose: () => stopEditingAndFocus(params),
            viewComponent: null,
          }}
          metadataId={metadata.guid}
        />
      ) : null;
    },
    cellStyle: (params) =>
      GridSmartTableProtocolValueService.getCellStyle(
        params,
        experimentOrProtocol,
        Metadata.entityName,
        metadataLabel,
        parentEntityClass,
      ),
  };
};
